/**
 * WizardStepPricing
 *
 * Step 3 of the launchpad creation wizard.
 * Configures royalty percentage and mint phase settings (prices and toggles).
 * This is a controlled component - parent handles persistence.
 */

import React from 'react';
import { TextField } from '../../../ui/components/TextField';
import {
  dropsToXrpTenths,
  xrpTenthsToDrops,
} from '../../../api/launches';

export interface PricingFormData {
  transferFee: number; // In basis points (e.g., 3000 = 3%)
  nftFlags: number; // XRPL NFT flags bitmask (bit 4 = tfMutable)
  allowlistEnabled: boolean;
  allowlistMintPriceDrops: number;
  allowlist2Enabled: boolean;
  allowlist2MintPriceDrops: number;
  mintPriceDrops: number;
  limitPerWallet: number | null;
}

export interface WizardStepPricingProps {
  value: PricingFormData;
  onChange: (data: PricingFormData) => void;
}

export const WizardStepPricing: React.FC<WizardStepPricingProps> = ({
  value: formData,
  onChange: setFormData,
}) => {
  const handleToggleAllowlist = (enabled: boolean) => {
    if (!enabled) {
      // Disable both phases
      setFormData({
        ...formData,
        allowlistEnabled: false,
        allowlist2Enabled: false,
      });
    } else {
      setFormData({
        ...formData,
        allowlistEnabled: true,
      });
    }
  };

  // Calculate public phase number based on enabled phases
  const getPublicPhaseNumber = () => {
    if (formData.allowlistEnabled && formData.allowlist2Enabled) return 3;
    if (formData.allowlistEnabled) return 2;
    return 1;
  };

  return (
    <div className="space-y-8">
      {/* Instructions */}
      <div className="p-4 rounded-lg bg-neutral-100 border border-neutral-border">
        <p className="text-body font-body text-default-font">
          Configure your mint pricing and phases. You can have up to 2 allowlist phases before the public mint.
          Allowlist addresses are managed from the dashboard after the wizard completes.
        </p>
      </div>

      {/* NFT Settings */}
      <div className="pb-6 border-b border-neutral-border">
        <h4 className="text-body-bold font-body-bold text-default-font mb-1">
          NFT Settings
        </h4>
        <p className="text-caption font-caption text-subtext-color mb-4">
          These settings apply to all NFTs in this collection and are locked after the draft stage.
        </p>

        <TextField
          label="Royalty (%)"
          helpText="Secondary sales royalty (0-50%). Examples: 3 for 3%, 2.5 for 2.5%"
        >
          <TextField.Input
            type="number"
            min="0"
            max="50"
            step="0.1"
            placeholder="0"
            value={String(formData.transferFee / 1000)}
            onChange={(e) =>
              setFormData({
                ...formData,
                transferFee: Math.round(parseFloat(e.target.value || '0') * 1000),
              })
            }
            onWheel={(e) => e.currentTarget.blur()}
          />
        </TextField>

        {/* Burnable by Issuer Toggle */}
        <div className="flex items-start gap-3 pt-2">
          <input
            type="checkbox"
            id="wizard-burnable-nfts"
            className="mt-1 h-4 w-4 rounded border-neutral-border accent-brand-500"
            checked={(formData.nftFlags & 1) !== 0}
            onChange={(e) => {
              const newFlags = e.target.checked
                ? formData.nftFlags | 1
                : formData.nftFlags & ~1;
              setFormData({ ...formData, nftFlags: newFlags });
            }}
          />
          <label htmlFor="wizard-burnable-nfts" className="flex flex-col gap-0.5 cursor-pointer">
            <span className="text-body font-body-bold text-default-font">
              Burnable by Issuer
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Allow the issuer to burn NFTs even after they've been transferred to holders. Holders can always burn their own NFTs regardless of this setting. This flag is permanent and cannot be changed after minting begins.
            </span>
          </label>
        </div>

        {/* Mutable NFTs Toggle */}
        <div className="flex items-start gap-3 pt-2">
          <input
            type="checkbox"
            id="wizard-mutable-nfts"
            className="mt-1 h-4 w-4 rounded border-neutral-border accent-brand-500"
            checked={(formData.nftFlags & 16) !== 0}
            onChange={(e) => {
              const newFlags = e.target.checked
                ? formData.nftFlags | 16
                : formData.nftFlags & ~16;
              setFormData({ ...formData, nftFlags: newFlags });
            }}
          />
          <label htmlFor="wizard-mutable-nfts" className="flex flex-col gap-0.5 cursor-pointer">
            <span className="text-body font-body-bold text-default-font">
              Mutable NFTs
            </span>
            <span className="text-caption font-caption text-subtext-color">
              Allow NFT metadata to be updated after minting. This flag is permanent and cannot be changed after minting begins.
            </span>
          </label>
        </div>
      </div>

      {/* Mint Phases */}
      <div>
        <h3 className="text-heading-3 font-heading-3 text-default-font mb-6">
          Mint Phases
        </h3>

        {/* Timeline Container */}
        <div className="relative pb-2">
          {/* Vertical Timeline Line */}
          <div className="absolute left-4 top-10 bottom-0 w-px bg-neutral-border" />

          {/* Phase 1: Allowlist */}
          <div className="relative flex gap-6 pb-8">
            {/* Timeline Node */}
            <div className="relative z-10 flex-shrink-0">
              {formData.allowlistEnabled ? (
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white text-body-bold font-body-bold">
                  1
                </div>
              ) : (
                <div className="w-8 h-8 rounded-full border-2 border-dashed border-neutral-400 dark:border-neutral-600 bg-transparent" />
              )}
            </div>

            {/* Phase Content */}
            <div className="flex-1 pt-1">
              <div className="flex items-center justify-between mb-4">
                <h4
                  className={`text-body-bold font-body-bold text-lg ${
                    formData.allowlistEnabled ? 'text-default-font' : 'text-subtext-color'
                  }`}
                >
                  Allowlist Phase
                  {!formData.allowlistEnabled && (
                    <span className="text-caption font-caption ml-2">(Skipped)</span>
                  )}
                </h4>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.allowlistEnabled}
                    onChange={(e) => handleToggleAllowlist(e.target.checked)}
                    className="w-5 h-5 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
                  />
                </label>
              </div>

              {formData.allowlistEnabled ? (
                <div className="space-y-4">
                  <TextField label="Price (XRP)" helpText="Set to 0 for free mint. Supports tenths (e.g. 2.5).">
                    <TextField.Input
                      type="number"
                      min="0"
                      step="0.1"
                      placeholder="0"
                      value={String(dropsToXrpTenths(formData.allowlistMintPriceDrops))}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowlistMintPriceDrops:
                            e.target.value === ''
                              ? 0
                              : xrpTenthsToDrops(parseFloat(e.target.value) || 0),
                        })
                      }
                    />
                  </TextField>
                </div>
              ) : (
                <p className="text-caption font-caption text-subtext-color">
                  Restrict early minting to specific addresses
                </p>
              )}
            </div>
          </div>

          {/* Phase 2: Allowlist 2 (Optional) - Only shown when Phase 1 is enabled */}
          {formData.allowlistEnabled && (
            <div className="relative flex gap-6 pb-8">
              {/* Timeline Node */}
              <div className="relative z-10 flex-shrink-0">
                {formData.allowlist2Enabled ? (
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white text-body-bold font-body-bold">
                    2
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded-full border-2 border-dashed border-neutral-400 dark:border-neutral-600 bg-transparent" />
                )}
              </div>

              {/* Phase Content */}
              <div className="flex-1 pt-1">
                <div className="flex items-center justify-between mb-4">
                  <h4
                    className={`text-body-bold font-body-bold text-lg ${
                      formData.allowlist2Enabled ? 'text-default-font' : 'text-subtext-color'
                    }`}
                  >
                    Allowlist Phase 2
                    {!formData.allowlist2Enabled && (
                      <span className="text-caption font-caption ml-2">(Skipped)</span>
                    )}
                  </h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.allowlist2Enabled}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          allowlist2Enabled: e.target.checked,
                        })
                      }
                      className="w-5 h-5 rounded border-neutral-300 text-brand-600 focus:ring-brand-500"
                    />
                  </label>
                </div>

                {formData.allowlist2Enabled ? (
                  <div className="space-y-4">
                    <TextField label="Price (XRP)" helpText="Set to 0 for free mint. Supports tenths (e.g. 2.5).">
                      <TextField.Input
                        type="number"
                        min="0"
                        step="0.1"
                        placeholder="0"
                        value={String(dropsToXrpTenths(formData.allowlist2MintPriceDrops))}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            allowlist2MintPriceDrops:
                              e.target.value === ''
                                ? 0
                                : xrpTenthsToDrops(parseFloat(e.target.value) || 0),
                          })
                        }
                      />
                    </TextField>
                  </div>
                ) : (
                  <p className="text-caption font-caption text-subtext-color">
                    Add a second whitelist phase with different pricing
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Public Phase */}
          <div className="relative flex gap-6">
            {/* Timeline Node */}
            <div className="relative z-10 flex-shrink-0">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-brand-500 text-white text-body-bold font-body-bold">
                {getPublicPhaseNumber()}
              </div>
            </div>

            {/* Phase Content */}
            <div className="flex-1 pt-1">
              <h4 className="text-body-bold font-body-bold text-default-font text-lg mb-4">
                Public Phase
              </h4>

              <div className="space-y-4">
                <TextField label="Price (XRP)" helpText="Set to 0 for free mint. Supports tenths (e.g. 2.5).">
                  <TextField.Input
                    type="number"
                    min="0"
                    step="0.1"
                    placeholder="0"
                    value={String(dropsToXrpTenths(formData.mintPriceDrops))}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        mintPriceDrops:
                          e.target.value === ''
                            ? 0
                            : xrpTenthsToDrops(parseFloat(e.target.value) || 0),
                      })
                    }
                  />
                </TextField>

                <TextField label="Max Per Wallet" helpText="Leave empty for unlimited.">
                  <TextField.Input
                    type="number"
                    min="1"
                    placeholder="Unlimited"
                    value={formData.limitPerWallet?.toString() ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        limitPerWallet: e.target.value === '' ? null : parseInt(e.target.value, 10),
                      })
                    }
                  />
                </TextField>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
